<template>
  <v-form @submit.prevent="handleInstall">
    <v-slide-x-transition hide-on-leave>
      <v-row v-show="!submitting">
        <v-col cols="12" md="8" class="pb-0">
          <gg-input-label for="select-app">Application</gg-input-label>
          <v-select
            v-model="selectedApp"
            :loading="loadingApps || loadingAppInfo"
            :items="appsList"
            dense
            outlined
            return-object
            id="select-app"
            placeholder="-- Select Application --"
            item-text="name"
            v-validate="'required'"
            data-vv-name="application"
            data-vv-as="Application"
            :error-messages="errors.collect('application')"
          >
            <template v-slot:selection="data">
              <div class="select-item" v-if="data.item && data.item.name">
                <v-img
                  :src="`/static/img/app-installer-icons/${data.item.logo}`"
                  contain
                  class="mr-2 select-logo"
                />
                <span class="select-name">{{ data.item.name }}</span>
              </div>
            </template>

            <template v-slot:item="data">
              <div class="select-item" v-bind="data.attrs" v-on="data.on">
                <v-img
                  :src="`/static/img/app-installer-icons/${data.item.logo}`"
                  contain
                  class="mr-2 select-logo"
                />
                <span class="select-name">{{ data.item.name }}</span>
              </div>
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" md="4" class="pb-0">
          <gg-select
            v-model="language"
            :items="languages"
            :disabled="noLangsFound"
            :loading="loadingAppInfo"
            :item-text="(item) => `${item.label} (${item.value})`"
            item-value="value"
            label="Language"
            v-validate="'required'"
            data-vv-name="language"
            data-vv-as="Language"
            :error-messages="errors.collect('language')"
          />
        </v-col>
        <v-scroll-y-transition>
          <v-col v-if="selectedApp" cols="12">
            <v-alert color="primary" outlined>
              <template v-slot:prepend>
                <v-img
                  :src="`/static/img/app-installer-icons/${selectedApp.logo}`"
                  contain
                  class="mr-2 app-installer-logo"
                />
              </template>

              <span>{{ selectedApp.description }}</span>
            </v-alert>
          </v-col>
        </v-scroll-y-transition>
        <v-col cols="12" md="4" class="pb-0">
          <gg-select
            v-model="protocol"
            :items="protocols"
            label="Protocol"
            v-validate="'required'"
            data-vv-name="protocol"
            data-vv-as="Protocol"
            :error-messages="errors.collect('protocol')"
          />
        </v-col>
        <v-col cols="12" md="8" class="pb-0">
          <gg-input-label>Domain</gg-input-label>
          <domain-select
            v-model="domain"
            no-label
            hide-details
            outlined
            solo
            flat
            @domain-type-selected="setDomainType"
          />
        </v-col>
        <v-col cols="12" md="6" class="pb-0">
          <gg-select
            v-model="subfolder"
            :items="pathItems"
            item-text="text"
            item-value="value"
            label="Installation Path"
          />
        </v-col>
        <v-col cols="12" md="6" class="pb-0">
          <gg-text-field
            v-if="subfolder"
            v-model="path"
            label="Directory"
            placeholder="e.g. blog"
            v-validate="'required|alpha_dash'"
            data-vv-name="directory"
            data-vv-as="Directory"
            :error-messages="errors.collect('directory')"
            @keyup.enter.prevent="handleInstall"
          />
        </v-col>
        <v-col cols="12" md="6" class="pb-0">
          <gg-text-field
            v-model="username"
            label="Admin Username"
            placeholder="e.g. admin"
            v-validate="'required|alpha_dash'"
            data-vv-name="username"
            data-vv-as="Admin Username"
            :error-messages="errors.collect('username')"
            @keyup.enter.prevent="handleInstall"
          />
        </v-col>
        <v-col cols="12" md="6" class="pb-0">
          <compact-password
            v-model="password"
            outlined
            charset="abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMOPRSTUVWXYZ0123456789-_@!"
            must-include-chars="-_@!"
            must-include-number
            autocomplete="new-password"
            label="Admin Password"
            v-validate="'required|min:8'"
            data-vv-name="password_adm"
            data-vv-as="Admin Password"
            :error-messages="errors.collect('password_adm')"
            with-generator
            @keyup.enter.prevent="handleInstall"
          />
        </v-col>
        <v-col cols="12" md="6" class="pb-0">
          <gg-text-field
            v-model="email"
            label="Admin Email"
            name="Admin Email"
            data-vv-as="Admin Email"
            v-validate="'required|email'"
            :error-messages="errors.collect('Admin Email')"
            @keyup.enter.prevent="handleInstall"
          />
        </v-col>
      </v-row>
    </v-slide-x-transition>
    <installation-progress v-if="submitting" />
    <v-row v-show="!submitting">
      <v-col cols="12">
        <gg-btn :loading="submitting" @click="handleInstall">
          Install Application
        </gg-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { SERVICE_TYPES } from '@/consts/service'
import { appPwdCharset, appUserCharset, domainTypeProtocolMap } from '@/consts/app-installer'
import { generateStr } from '@/helpers/helpers'
import DomainSelect from '@/components/Common/DomainSelect'
import CompactPassword from '@/components/Common/password/CompactPassword'
import InstallationProgress from '@/components/Services/Hosting/Shared/Installer/InstallationProgress'

const pathItems = [
  { text: 'Main Folder', value: false },
  { text: 'Sub-folder', value: true },
]

export default {
  name: 'InstallAppForm',
  components: {
    DomainSelect,
    CompactPassword,
    InstallationProgress,
  },
  data () {
    return {
      subfolder: false,
      pathItems,
    }
  },
  computed: {
    ...mapGetters({
      // model getters
      getSelectedApp: 'app_installer/selectedApp',
      getProtocol: 'app_installer/protocol',
      getDomain: 'app_installer/domain',
      getLanguage: 'app_installer/language',
      getPath: 'app_installer/path',
      getUsername: 'app_installer/username',
      getPassword: 'app_installer/password',
      getEmail: 'app_installer/email',
      // regular getters
      protocols: 'app_installer/protocols',
      submitting: 'app_installer/submitting',
      availableAppsList: 'app_installer/availableAppsList',
      loadingApps: 'app_installer/loadingApps',
      languages: 'app_installer/languages',
      noLangsFound: 'app_installer/noLangsFound',
      loadingAppInfo: 'app_installer/loadingAppInfo',
      hosting: 'hosting',
    }),
    /**
     * Generate two-way bindings to the store getters/mutations,
     * so we can use v-model on them directly:
     */
    ...(() => {
      const fields = ['selectedApp', 'protocol', 'domain', 'language', 'path', 'username', 'password', 'email']
      const obj = {}
      fields.forEach(field => {
        const getterName = 'get' + field.charAt(0).toUpperCase() + field.slice(1)
        const mutationName = 'set' + field.charAt(0).toUpperCase() + field.slice(1)
        obj[field] = {
          get () { return this[getterName] },
          set (value) { this[mutationName](value) }
        }
      })
      return obj
    })(),

    isResellerOwner () {
      return this.hosting.type == SERVICE_TYPES.RESELLER && this.$route.query.user == this.hosting.username
    },
    appsList () {
      const list = this.availableAppsList.slice().sort((a, b) => a.name.localeCompare(b.name)) || []
      return this.isResellerOwner ? list : list.filter(app => !app.resellerOnly)
    },
  },
  methods: {
    ...mapMutations({
      setSelectedApp: 'app_installer/SET_SELECTED_APP',
      setProtocol: 'app_installer/SET_PROTOCOL',
      setDomain: 'app_installer/SET_DOMAIN',
      setLanguage: 'app_installer/SET_LANGUAGE',
      setPath: 'app_installer/SET_PATH',
      setUsername: 'app_installer/SET_USERNAME',
      setPassword: 'app_installer/SET_PASSWORD',
      setEmail: 'app_installer/SET_EMAIL',
      resetFormState: 'app_installer/RESET_FORM_STATE',
    }),
    ...mapActions({
      getAppsList: 'app_installer/getAppsList',
      fetchSoftInfo: 'app_installer/fetchSoftInfo',
      installApp: 'app_installer/installApp',
    }),
    /**
     * @param domainType DOMAIN_TYPES.MAIN | DOMAIN_TYPES.SUB | DOMAIN_TYPES.ADDON Domain type
     */
    setDomainType (domainType) {
      this.setProtocolForDomainType(domainType)
    },
    setProtocolForDomainType (domainType) {
      const protocol = domainTypeProtocolMap.get(domainType)
      if (protocol) {
        this.setProtocol(protocol)
      }
    },
    async handleInstall () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      const dbPwd = generateStr(12, appPwdCharset)
      const dbUser = `u${generateStr(8, appUserCharset)}`
      const dbPref = `${generateStr(3, 'abcdefghijklmnopqrstuvwxyz')}_`
      const dbName = `db_${generateStr(4, appUserCharset)}`
      const params = {
        username: this.$route.query.user,
        softdb: dbName,
        dbusername: dbUser,
        dbuserpass: dbPwd,
        dbprefix: dbPref,
        hostname: 'localhost',
        site_name: 'My Website',
        site_desc: 'My Website Description',
      }
      const { done } = await this.installApp(params) // handle failure
      if (done) {
        this.$emit('app-installed')
      }
    },
  },
  mounted () {
    this.resetFormState()

    this.getAppsList({ username: this.$route.query.user })
  },
  watch: {
    'selectedApp.id' (newId) {
      if (newId) {
        this.fetchSoftInfo({ username: this.$route.query.user, softId: newId })
      }
    },
  }
}
</script>

<style scoped>
.app-installer-logo {
  width: 32px !important;
  height: 32px !important;
  max-width: 32px !important;
  max-height: 32px !important;
  object-fit: contain;
  display: inline-block;
}

/* Select App icon styles */
.select-logo {
  width: 16px !important;
  height: 16px !important;
  max-width: 16px !important;
  max-height: 16px !important;
  object-fit: contain;
  display: inline-block;
}

.select-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.select-name {
  display: inline-block;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}
</style>
